import Grid from "@mui/material/Grid2";
import { isEqual, omit } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useIsOwnerOrSuperAdmin } from "@/auth/hooks";
import AppFullPageTabs from "@/components/app-full-page-tabs";
import {
  TotalsPage,
  defaultValues,
} from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/totals-page";
import { HandHistoryExportsPage } from "@/module/service-configs-module/statistics-visualization-module/hand-history-exports/hand-history-exports-page";
import { TradingPage } from "@/module/service-configs-module/statistics-visualization-module/second-timings/trading-page";
import { StatDiversityPage } from "@/module/service-configs-module/statistics-visualization-module/stat-diversity/stat-diversity-page";
import { StatisticVisualizationFilters } from "@/module/service-configs-module/statistics-visualization-module/statistic-visualization-filters";
import { TimingsPage } from "@/module/service-configs-module/statistics-visualization-module/timings/timings-page";
import { Route } from "@/routes/statanalytics";
import { MoneyFlowRequestDto } from "@/shared/api";

const StatisticsVisualizationModule: React.FC = () => {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const isOwner = useIsOwnerOrSuperAdmin();
  const { statserviceVisualizationTab: tabValue } = Route.useSearch();

  const [totalPageFilter, setTotalPageFilter] = useState<MoneyFlowRequestDto | null>(null);
  const [tradingPageFilter, setTradingPageFilter] = useState<MoneyFlowRequestDto | null>(null);
  const [statDiversityFilter, setStatDiversityFilter] = useState<MoneyFlowRequestDto | null>(
    null
  );
  const [timingFilter, setTimingFilter] = useState<MoneyFlowRequestDto | null>(null);
  const [currentFilter, setCurrentFilter] = useState<MoneyFlowRequestDto>(defaultValues);

  const handleSubmit = (data: MoneyFlowRequestDto) => {
    switch (tabValue) {
      case 0:
        setTotalPageFilter(structuredClone({ ...data }));
        break;
      case 1:
        setTradingPageFilter(structuredClone({ ...data, salt: +new Date() }));
        break;
      case 2:
        setStatDiversityFilter(structuredClone({ ...data, salt: +new Date() }));
        break;
      case 3:
        setTimingFilter(structuredClone({ ...data, salt: +new Date() }));
        break;
    }
  };

  const setTabValue = (value: number) => {
    void navigate({ search: { statserviceVisualizationTab: value } });
  };

  const [
    isTotalPageDataOutdated,
    isTradingPageDataOutdated,
    isStatDiversityDataOutdated,
    isTimingFilterOutdated,
  ] = useMemo(() => {
    return [
      totalPageFilter ? !isEqual(omit({ ...totalPageFilter }, "salt"), currentFilter) : false,
      tradingPageFilter
        ? !isEqual(omit({ ...tradingPageFilter }, "salt"), currentFilter)
        : false,
      statDiversityFilter
        ? !isEqual(omit({ ...statDiversityFilter }, "salt"), currentFilter)
        : false,
      timingFilter ? !isEqual(omit({ ...timingFilter }, "salt"), currentFilter) : false,
    ];
  }, [totalPageFilter, tradingPageFilter, statDiversityFilter, timingFilter, currentFilter]);

  const handleFiltersChange = useCallback((data: MoneyFlowRequestDto) => {
    setCurrentFilter(structuredClone(data));
  }, []);

  return (
    <Grid container rowSpacing={2} columnSpacing={2} sx={{ position: "relative" }}>
      <Grid size={{ xs: 12, md: 12 }}>
        <AppFullPageTabs
          onTabChange={(val) => setTabValue(val)}
          defaultValue={tabValue}
          items={[
            {
              value: 0,
              label: t("main"),
              render: () => (
                <TotalsPage
                  filter={totalPageFilter}
                  isDataOutdated={isTotalPageDataOutdated}
                />
              ),
              keepMounted: true,
              //condition: () => !!0/*rulesPerm.read*/,
            },
            {
              value: 1,
              label: t("timings"),
              render: () => (
                <TradingPage
                  filters={tradingPageFilter}
                  isDataOutdated={isTradingPageDataOutdated}
                />
              ),
              keepMounted: true,
              //condition: () => !!0/*chatsPerms.read*/,
            },
            {
              value: 2,
              label: t("statDiversity"),
              keepMounted: true,
              render: () => (
                <StatDiversityPage
                  filters={statDiversityFilter}
                  isDataOutdated={isStatDiversityDataOutdated}
                  isActive={tabValue === 2}
                />
              ),
              //condition: () => !!0/*chatsPerms.read*/,
            },
            {
              value: 3,
              label: t("timings"),
              render: () => (
                <TimingsPage filters={timingFilter} isDataOutdated={isTimingFilterOutdated} />
              ),
              //condition: () => !!0/*chatsPerms.read*/,
            },
            {
              value: 4,
              label: t("handExports"),
              render: () => <HandHistoryExportsPage />,
              condition: () => isOwner,
            },
          ]}
        />
      </Grid>
      <StatisticVisualizationFilters
        initialState={defaultValues}
        onSubmit={handleSubmit}
        onChange={handleFiltersChange}
        isStatsFilters={tabValue === 2}
        isHandExportTab={isOwner && tabValue === 4}
      />
    </Grid>
  );
};

export default StatisticsVisualizationModule;
